import React, { useState, useEffect } from "react";
import { Text, Tooltip } from "@fluentui/react-components";
import { TeamConfig } from "../../models/Team";

// Nolij AI Models with their icons and descriptions
const nolijModels = [
  {
    id: 'nolij-sage',
    name: 'Nolij-Sage',
    description: 'Optimized for Financial Planning',
    icon: 'https://img.icons8.com/color/96/crystal-ball.png'
  },
  {
    id: 'nolij-logic',
    name: 'Nolij-Logic',
    description: 'Optimized for System Integration',
    icon: 'https://img.icons8.com/color/96/light-on.png'
  },
  {
    id: 'nolij-analyzer',
    name: 'Nolij-Analyzer',
    description: 'Optimized for Financial Analysis',
    icon: 'https://img.icons8.com/color/96/microscope.png'
  }
];

// System Integrations
const systemIntegrations = [
  {
    id: 'dynamics',
    name: 'Dynamics 365',
    description: 'ERP System',
    icon: 'https://img.icons8.com/color/96/microsoft.png'
  },
  {
    id: 'rossum',
    name: 'Rossum',
    description: 'Invoice Ingestion',
    icon: 'https://img.icons8.com/color/96/document.png'
  },
  {
    id: 'billcom',
    name: 'Bill.com',
    description: 'Payment Processing',
    icon: 'https://img.icons8.com/color/96/money-transfer.png'
  }
];

const sidebarStyles: React.CSSProperties = {
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  padding: '16px 8px',
  gap: '20px',
  backgroundColor: 'var(--colorNeutralBackground2)',
  borderRadius: '8px',
  minWidth: '70px',
  maxWidth: '70px'
};

const itemStyles: React.CSSProperties = {
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  gap: '4px',
  cursor: 'default'
};

const iconStyles: React.CSSProperties = {
  width: '36px',
  height: '36px',
  borderRadius: '8px',
  backgroundColor: 'var(--colorNeutralBackground1)',
  padding: '6px',
  boxShadow: '0 1px 3px rgba(0,0,0,0.1)'
};

const labelStyles: React.CSSProperties = {
  fontSize: '10px',
  fontWeight: '500',
  color: 'var(--colorNeutralForeground2)',
  textAlign: 'center',
  lineHeight: '1.2',
  maxWidth: '60px',
  wordWrap: 'break-word'
};

const headerStyles: React.CSSProperties = {
  fontSize: '9px',
  fontWeight: '600',
  color: 'var(--colorNeutralForeground3)',
  textTransform: 'uppercase',
  letterSpacing: '0.5px',
  marginBottom: '4px'
};

// Token Meter Component with animation
interface TokenMeterProps {
  isActive?: boolean; // True when agents are streaming/working
}

// Latest hyperscaler model pricing (Jan 2026, per 1K tokens, blended input/output)
// GPT-4o: $2.50/1M input, $10.00/1M output → ~$6.25/1M blended = $0.00625/1K
// Claude 3.5 Sonnet: $3.00/1M input, $15.00/1M output → ~$9.00/1M blended = $0.009/1K
// Gemini 1.5 Pro: $1.25/1M input, $5.00/1M output → ~$3.125/1M blended = $0.003125/1K
const hyperscalerModels = [
  { name: 'GPT-4o', provider: 'OpenAI', costPer1K: 0.00625, color: '#10a37f' },
  { name: 'Claude 3.5', provider: 'Anthropic', costPer1K: 0.009, color: '#d97706' },
  { name: 'Gemini 1.5', provider: 'Google', costPer1K: 0.003125, color: '#4285f4' },
];

const TokenMeter: React.FC<TokenMeterProps> = ({ isActive = false }) => {
  const [tokens, setTokens] = useState(0);
  const [isAnimating, setIsAnimating] = useState(false);
  
  // Animate tokens when active (agents working)
  useEffect(() => {
    if (isActive) {
      setIsAnimating(true);
      const interval = setInterval(() => {
        setTokens(prev => prev + Math.floor(Math.random() * 50) + 10);
      }, 200);
      
      return () => clearInterval(interval);
    } else {
      setIsAnimating(false);
    }
  }, [isActive]);
  
  // Initial animation on mount
  useEffect(() => {
    if (!isActive) {
      const targetTokens = 8450 + Math.floor(Math.random() * 2000);
      const duration = 2000;
      const steps = 30;
      const increment = targetTokens / steps;
      let current = 0;
      
      setIsAnimating(true);
      const interval = setInterval(() => {
        current += increment;
        if (current >= targetTokens) {
          setTokens(targetTokens);
          setIsAnimating(false);
          clearInterval(interval);
        } else {
          setTokens(Math.floor(current));
        }
      }, duration / steps);
      
      return () => clearInterval(interval);
    }
  }, []);
  
  return (
    <div style={{
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      padding: '10px 6px',
      backgroundColor: 'var(--colorNeutralBackground1)',
      borderRadius: '8px',
      border: '1px solid var(--colorNeutralStroke2)',
      gap: '8px',
      marginTop: '8px',
      minWidth: '58px'
    }}>
      {/* Title */}
      <Text style={{
        fontSize: '9px',
        fontWeight: '600',
        color: 'var(--colorNeutralForeground3)',
        textTransform: 'uppercase',
        letterSpacing: '0.5px'
      }}>
        Tokens
      </Text>
      
      {/* Token counter with animation */}
      <div style={{
        display: 'flex',
        alignItems: 'center',
        gap: '3px'
      }}>
        <span style={{ 
          fontSize: '10px',
          color: isAnimating ? 'var(--colorBrandForeground1)' : 'var(--colorNeutralForeground2)',
          transition: 'color 0.3s'
        }}>
          {isAnimating ? '⚡' : '📊'}
        </span>
        <Text style={{
          fontSize: '11px',
          fontWeight: '600',
          color: 'var(--colorNeutralForeground1)',
          fontFamily: 'monospace'
        }}>
          {tokens.toLocaleString()}
        </Text>
      </div>
      
      {/* Animated progress bar */}
      <div style={{
        width: '46px',
        height: '3px',
        backgroundColor: 'var(--colorNeutralBackground4)',
        borderRadius: '2px',
        overflow: 'hidden'
      }}>
        <div style={{
          width: isAnimating ? '100%' : '60%',
          height: '100%',
          backgroundImage: isAnimating 
            ? 'linear-gradient(90deg, var(--colorBrandBackground), var(--colorBrandBackgroundHover), var(--colorBrandBackground))'
            : 'none',
          backgroundColor: isAnimating ? 'transparent' : 'var(--colorBrandBackground)',
          borderRadius: '2px',
          transition: 'width 0.3s ease-out',
          backgroundSize: isAnimating ? '200% 100%' : 'auto',
          animation: isAnimating ? 'shimmer 1.5s infinite' : 'none'
        }} />
      </div>
      
      {/* Nolij Cost - FREE */}
      <div style={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        padding: '6px 4px',
        backgroundColor: 'rgba(16, 185, 129, 0.1)',
        borderRadius: '6px',
        width: '100%'
      }}>
        <Text style={{
          fontSize: '8px',
          color: 'var(--colorNeutralForeground3)',
          marginBottom: '2px'
        }}>
          Nolij
        </Text>
        <Text style={{
          fontSize: '13px',
          fontWeight: '700',
          color: '#10b981',
          fontFamily: 'monospace'
        }}>
          $0.00
        </Text>
      </div>
      
      {/* Divider */}
      <div style={{
        width: '100%',
        height: '1px',
        backgroundColor: 'var(--colorNeutralStroke2)'
      }} />
      
      {/* Hyperscaler costs comparison */}
      <Text style={{
        fontSize: '7px',
        color: 'var(--colorNeutralForeground4)',
        textTransform: 'uppercase',
        letterSpacing: '0.3px'
      }}>
        If using
      </Text>
      
      {hyperscalerModels.map((model) => {
        const cost = (tokens / 1000) * model.costPer1K;
        return (
          <div key={model.name} style={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            width: '100%'
          }}>
            <Text style={{
              fontSize: '7px',
              color: 'var(--colorNeutralForeground4)'
            }}>
              {model.name}
            </Text>
            <Text style={{
              fontSize: '10px',
              fontWeight: '600',
              color: model.color,
              fontFamily: 'monospace',
              transition: 'all 0.2s'
            }}>
              ${cost.toFixed(2)}
            </Text>
          </div>
        );
      })}
    </div>
  );
};

interface WorkspaceSidebarsProps {
  showIntegrations?: boolean;
  showModels?: boolean;
  isAgentActive?: boolean; // True when agents are streaming/working
  team?: TeamConfig | null;
}

export const IntegrationsSidebar: React.FC<{ team?: TeamConfig | null }> = ({ team }) => {
  // Use team's ui_config.integrations if available, otherwise fall back to defaults
  const integrations = team?.ui_config?.integrations || systemIntegrations;

  return (
    <div style={sidebarStyles}>
      <Text style={headerStyles}>Systems</Text>
      {integrations.map((integration) => (
        <Tooltip
          key={integration.id}
          content={`${integration.name} - ${integration.description}`}
          relationship="description"
          positioning="after"
        >
          <div style={itemStyles}>
            <img
              src={integration.icon}
              alt={integration.name}
              style={iconStyles}
              onError={(e) => {
                const target = e.target as HTMLImageElement;
                target.style.display = 'none';
              }}
            />
            <Text style={labelStyles}>{integration.name.split(' ')[0]}</Text>
          </div>
        </Tooltip>
      ))}
    </div>
  );
};

export const ModelsSidebar: React.FC<{ isAgentActive?: boolean }> = ({ isAgentActive = false }) => {
  return (
    <div style={sidebarStyles}>
      <Text style={headerStyles}>AI Models</Text>
      {nolijModels.map((model) => (
        <Tooltip
          key={model.id}
          content={`${model.name} - ${model.description}`}
          relationship="description"
          positioning="before"
        >
          <div style={itemStyles}>
            <img
              src={model.icon}
              alt={model.name}
              style={iconStyles}
              onError={(e) => {
                const target = e.target as HTMLImageElement;
                target.style.display = 'none';
              }}
            />
            <Text style={labelStyles}>{model.name.replace('Nolij-', '')}</Text>
          </div>
        </Tooltip>
      ))}
      
      {/* Token Meter */}
      <TokenMeter isActive={isAgentActive} />
    </div>
  );
};

const WorkspaceSidebars: React.FC<WorkspaceSidebarsProps> = ({
  showIntegrations = true,
  showModels = true,
  team
}) => {
  return (
    <>
      {showIntegrations && <IntegrationsSidebar team={team} />}
      {showModels && <ModelsSidebar />}
    </>
  );
};

export default WorkspaceSidebars;
